import { useState } from "react";
import { Sidebar } from "@/components/layout/sidebar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { FileUpload } from "@/components/file-upload";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Loader2, Upload, Plus, ChevronLeft, ChevronRight, Download, AlertTriangle, ChevronDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { apiRequest } from "@/lib/queryClient";
import { useLocation } from "wouter";
import { ScreenplayCard } from "@/components/screenplay-card";
import { RenderJobsList } from "@/components/render-jobs-list";
import { Screenplay, RenderJob } from "@/lib/user-types";

export default function DashboardPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [rightsOwnership, setRightsOwnership] = useState(false);

  // Reset form when dialog opens/closes
  const resetForm = () => {
    setTitle("");
    setSelectedFile(null);
    setRightsOwnership(false);
    setModerationError(null);
  };
  const [screenplayPage, setScreenplayPage] = useState(0);
  const screenplaysPerPage = 4;
  const [moderationError, setModerationError] = useState<{
    canProceed: boolean;
    flaggedSections: {
      startBlockId: string;
      endBlockId: string;
      text: string;
      level: "allow" | "warning" | "block";
      categories: string[];
    }[];
  } | null>(null);
  
  // Fetch user's screenplays
  const { 
    data: screenplays = [], 
    isLoading: isLoadingScreenplays 
  } = useQuery<Screenplay[]>({
    queryKey: ["/api/screenplays"]
  });

  // Fetch user's renders
  const { 
    data: renderJobs = [], 
    isLoading: isLoadingRenderJobs 
  } = useQuery<RenderJob[]>({
    queryKey: ["/api/renders"]
  });

  // Fetch user's points
  const { data: userPoints = 0 } = useQuery<number>({
    queryKey: ["/api/user/points"]
  });

  // Calculate pagination for screenplays
  const totalPages = Math.ceil(screenplays.length / screenplaysPerPage);
  const startIndex = screenplayPage * screenplaysPerPage;
  const endIndex = startIndex + screenplaysPerPage;
  const currentScreenplays = screenplays.slice(startIndex, endIndex);

  // Pagination handlers
  const goToPreviousPage = () => {
    setScreenplayPage(prev => Math.max(0, prev - 1));
  };

  const goToNextPage = () => {
    setScreenplayPage(prev => Math.min(totalPages - 1, prev + 1));
  };

  // Mutation for screenplay upload
  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFile) {
        throw new Error("No file selected");
      }
      
      const formData = new FormData();
      formData.append("title", title || selectedFile.name.replace(/\.[^/.]+$/, ""));
      formData.append("file", selectedFile);
      
      const response = await fetch("/api/screenplays", {
        method: "POST",
        body: formData,
      });
      
      if (response.status === 403) {
        const moderationData = await response.json();
        throw new Error("MODERATION_FAILED", { cause: moderationData });
      }
      
      if (!response.ok) {
        throw new Error("Upload failed");
      }
      
      return await response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/screenplays"] });
      setIsUploadOpen(false);
      setTitle("");
      setSelectedFile(null);
      setModerationError(null);
      toast({
        title: "Upload successful",
        description: "Your screenplay has been uploaded successfully.",
      });
      navigate(`/screenplays/${data.id}`);
    },
    onError: (error: Error) => {
      if (error.message === "MODERATION_FAILED" && error.cause) {
        setModerationError(error.cause as any);
      } else {
        toast({
          title: "Upload failed",
          description: error.message,
          variant: "destructive",
        });
      }
    }
  });

  const handleUpload = (e: React.FormEvent) => {
    e.preventDefault();
    uploadMutation.mutate();
  };

  // Download moderation report as formatted text
  const downloadModerationReport = () => {
    if (!moderationError) return;
    
    const report = `Content Moderation Report
Generated: ${new Date().toLocaleString()}
Screenplay: ${title || selectedFile?.name || 'Untitled'}

SUMMARY:
Can Proceed: ${moderationError.canProceed ? 'Yes' : 'No'}
Total Flagged Sections: ${moderationError.flaggedSections.length}

FLAGGED CONTENT DETAILS:
${moderationError.flaggedSections.map((section, index) => `
Section ${index + 1}:
- Level: ${section.level.toUpperCase()}
- Categories: ${section.categories.join(', ')}
- Content:
${section.text}

${'='.repeat(80)}
`).join('')}

This report contains details about content that may violate our content policy. Please review and modify your screenplay accordingly before attempting to upload again.
`;

    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `moderation-report-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1 ml-0 md:ml-64">
        <main className="py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
              <div className="mt-4 sm:mt-0 flex space-x-3">
                <Dialog open={isUploadOpen} onOpenChange={(open) => {
                  setIsUploadOpen(open);
                  if (!open) resetForm();
                }}>
                  <DialogTrigger asChild>
                    <Button>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Screenplay
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Upload Screenplay</DialogTitle>
                      <DialogDescription>
                        Upload your screenplay in XML or PDF format. The file will be processed and prepared for rendering.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleUpload}>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="title">Title (Optional)</Label>
                          <Input 
                            id="title" 
                            value={title} 
                            onChange={(e) => setTitle(e.target.value)} 
                            placeholder="Enter a title or use filename"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Screenplay File (XML or PDF)</Label>
                          <FileUpload 
                            accept=".pdf,.xml"
                            onFileSelected={setSelectedFile}
                          />
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="rights-ownership"
                            checked={rightsOwnership}
                            onCheckedChange={(checked) => setRightsOwnership(checked === true)}
                          />
                          <Label 
                            htmlFor="rights-ownership" 
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            I own the rights to this material or have permission from the owner to produce this content.
                          </Label>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => {
                            setIsUploadOpen(false);
                            resetForm();
                          }}
                        >
                          Cancel
                        </Button>
                        <Button 
                          type="submit" 
                          disabled={!selectedFile || !rightsOwnership || uploadMutation.isPending}
                        >
                          {uploadMutation.isPending ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Uploading...
                            </>
                          ) : "Upload"}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>

                {/* Content Moderation Error Dialog */}
                <Dialog open={!!moderationError} onOpenChange={() => setModerationError(null)}>
                  <DialogContent className="max-w-4xl max-h-[90vh]">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-red-500" />
                        Content Policy Violation Detected
                      </DialogTitle>
                      <DialogDescription>
                        Your screenplay contains content that may violate our content policy. Please review the flagged sections below and make necessary adjustments.
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                      {moderationError && (
                        <>
                          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="font-medium text-red-800">Summary</h3>
                              <Badge variant={moderationError.canProceed ? "secondary" : "destructive"}>
                                {moderationError.canProceed ? "Can Proceed with Warnings" : "Upload Blocked"}
                              </Badge>
                            </div>
                            <p className="text-sm text-red-700">
                              Found {moderationError.flaggedSections.filter(s => s.level === "block").length} blocked sections
                              and {moderationError.flaggedSections.filter(s => s.level === "warning").length} warning sections.
                            </p>
                          </div>

                          <div>
                            <h3 className="font-medium mb-3">Flagged Sections (Level: "blocked")</h3>
                            <ScrollArea className="h-96 border rounded-lg">
                              <div className="p-4 space-y-4">
                                {moderationError.flaggedSections
                                  .filter(section => section.level === "block")
                                  .map((section, index) => (
                                    <Card key={index} className="border-red-200">
                                      <CardHeader className="pb-2">
                                        <div className="flex items-center justify-between">
                                          <CardTitle className="text-sm">
                                            Section {index + 1}
                                          </CardTitle>
                                          <div className="flex gap-1">
                                            {section.categories.map((category) => (
                                              <Badge key={category} variant="destructive" className="text-xs">
                                                {category}
                                              </Badge>
                                            ))}
                                          </div>
                                        </div>
                                      </CardHeader>
                                      <CardContent>
                                        <Collapsible>
                                          <CollapsibleTrigger asChild>
                                            <Button variant="ghost" size="sm" className="w-full justify-between">
                                              <span>View Content</span>
                                              <ChevronDown className="h-4 w-4" />
                                            </Button>
                                          </CollapsibleTrigger>
                                          <CollapsibleContent>
                                            <ScrollArea className="h-32 mt-2 p-3 bg-gray-50 rounded border text-sm">
                                              {section.text}
                                            </ScrollArea>
                                          </CollapsibleContent>
                                        </Collapsible>
                                      </CardContent>
                                    </Card>
                                  ))}
                                
                                {moderationError.flaggedSections.filter(s => s.level === "block").length === 0 && (
                                  <p className="text-center text-gray-500 py-8">
                                    No blocked sections found.
                                  </p>
                                )}
                              </div>
                            </ScrollArea>
                          </div>
                        </>
                      )}
                    </div>

                    <DialogFooter className="flex justify-between">
                      <Button 
                        variant="outline" 
                        onClick={downloadModerationReport}
                        className="flex items-center gap-2"
                      >
                        <Download className="h-4 w-4" />
                        Download Full Report
                      </Button>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          onClick={() => setModerationError(null)}
                        >
                          Close
                        </Button>
                        <Button 
                          onClick={() => {
                            setModerationError(null);
                            setIsUploadOpen(true);
                          }}
                        >
                          Try Again with Modified Content
                        </Button>
                      </div>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
            <div className="py-4">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {/* Recent Screenplays */}
                <Card className="col-span-1 sm:col-span-2">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle>Recent Screenplays</CardTitle>
                      {screenplays.length > screenplaysPerPage && (
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={goToPreviousPage}
                            disabled={screenplayPage === 0}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="text-sm text-gray-500">
                            {screenplayPage + 1} of {totalPages}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={goToNextPage}
                            disabled={screenplayPage >= totalPages - 1}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isLoadingScreenplays ? (
                      <div className="flex items-center justify-center h-32">
                        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                      </div>
                    ) : screenplays.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {currentScreenplays.map((screenplay) => (
                          <ScreenplayCard key={screenplay.id} screenplay={screenplay} />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-gray-500">You haven't uploaded any screenplays yet.</p>
                        <Button 
                          variant="outline" 
                          className="mt-4"
                          onClick={() => setIsUploadOpen(true)}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Upload your first screenplay
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Activity Summary */}
                <Card className="col-span-1">
                  <CardHeader>
                    <CardTitle>Activity Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Screenplays</span>
                        <span className="font-medium">{screenplays.length}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Render Jobs</span>
                        <span className="font-medium">{renderJobs.length}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Points</span>
                        <span className="font-medium">{userPoints.toLocaleString()}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Recent Render Jobs */}
              <Card className="mt-6">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                      <CardTitle>Recent Renders</CardTitle>
                      <span className="text-sm text-gray-500">(Sorted by newest first)</span>
                    </div>
                    {renderJobs.length > 5 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-primary hover:text-primary/80"
                        onClick={() => navigate('/renders')}
                      >
                        Show All
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <RenderJobsList 
                    renderJobs={[...renderJobs].sort((a, b) => 
                      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                    )}
                    isLoading={isLoadingRenderJobs}
                    itemsPerPage={5}
                    emptyMessage="You haven't created any renders yet. Upload a screenplay and start rendering to see them here."
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}